import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Search, Shield, Calculator, Building, UserCheck, Receipt, Phone } from "lucide-react";
import Navigation from "@/components/Navigation";
import DueDiligenceStep from "@/components/due-diligence/DueDiligenceStep";
import ProtectionItem from "@/components/due-diligence/ProtectionItem";
import TimelineStep from "@/components/due-diligence/TimelineStep";
import RedFlagsSection from "@/components/due-diligence/RedFlagsSection";

const DueDiligence = () => {
  const steps = [
    {
      icon: <FileText className="h-8 w-8 text-blue-600" />,
      title: "Title Work",
      description: "We order comprehensive title searches to ensure clear ownership and identify any liens, encumbrances, or title defects that could affect your investment.",
      details: "Our title work includes a complete chain of title review, lien searches, and verification of current ownership status."
    },
    {
      icon: <Calculator className="h-8 w-8 text-green-600" />,
      title: "BPO Appraisal",
      description: "A Broker Price Opinion (BPO) appraisal is conducted to determine the current market value of the property securing your investment.",
      details: "This independent valuation ensures your investment-to-value ratio provides adequate security and helps confirm appropriate pricing."
    },
    {
      icon: <Search className="h-8 w-8 text-purple-600" />,
      title: "Tax Verification",
      description: "We verify current property tax status, payment history, and any outstanding tax obligations that could impact the investment.",
      details: "This includes checking for delinquent taxes, special assessments, and ensuring tax responsibilities are clearly defined in the contract."
    },
    {
      icon: <Receipt className="h-8 w-8 text-amber-600" />,
      title: "Payment History Review",
      description: "We collect and verify proof of the last 12 installment payments made on the land contract to ensure payment consistency.",
      details: "This documentation confirms the borrower's payment reliability and helps assess the ongoing performance of the land contract."
    },
    {
      icon: <Phone className="h-8 w-8 text-teal-600" />,
      title: "Purchaser Interview",
      description: "We conduct a direct phone interview with the land contract purchaser to assess their financial stability and communicate the ownership transfer.",
      details: "This conversation helps us evaluate their ability to continue making payments and ensures they understand that we are purchasing the land contract from the original seller."
    },
    {
      icon: <Building className="h-8 w-8 text-orange-600" />,
      title: "Title Company Closing",
      description: "All transactions are closed through a licensed title company to ensure proper documentation and legal compliance.",
      details: "The title company facilitates the closing process, handles document recording, and provides title insurance for your protection."
    }
  ];

  const protections = [
    {
      icon: <Shield className="h-6 w-6 text-green-600" />,
      title: "Clean Title Guarantee",
      description: "Every investor receives an assignment of land contract with verified clean title, free from undisclosed liens or encumbrances."
    },
    {
      icon: <UserCheck className="h-6 w-6 text-green-600" />,
      title: "Professional Oversight",
      description: "Licensed title companies oversee all closings, ensuring compliance with state regulations and proper documentation."
    },
    {
      icon: <FileText className="h-6 w-6 text-green-600" />,
      title: "Complete Documentation",
      description: "All necessary legal documents are properly prepared, executed, and recorded to protect your investment rights."
    }
  ];

  const timelineSteps = [
    {
      timeframe: "1-3",
      title: "Days 1-3: Title Search & BPO",
      description: "We initiate title work and order the BPO appraisal immediately upon contract acceptance.",
      bgColor: "bg-blue-100",
      textColor: "text-blue-600"
    },
    {
      timeframe: "3-5",
      title: "Days 3-5: Tax & Legal Review",
      description: "Complete tax verification and legal document review to identify any potential issues.",
      bgColor: "bg-purple-100",
      textColor: "text-purple-600"
    },
    {
      timeframe: "7-10",
      title: "Days 7-10: Closing",
      description: "Schedule and complete closing at the title company, with all documents properly executed and recorded.",
      bgColor: "bg-green-100",
      textColor: "text-green-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation />
      
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Our Due Diligence Process
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We conduct thorough due diligence on every investment opportunity to protect our investors 
              and ensure secure, properly documented transactions.
            </p>
          </div>

          {/* Process Steps */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Our Comprehensive Review Process
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {steps.map((step, index) => (
                <DueDiligenceStep
                  key={index}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                  details={step.details}
                  stepNumber={index + 1}
                />
              ))}
            </div>
          </div>

          {/* What You Get */}
          <Card className="mb-8 bg-gradient-to-r from-green-50 to-blue-50">
            <CardHeader>
              <CardTitle className="text-2xl text-center">What You Receive</CardTitle>
              <CardDescription className="text-center text-lg">
                Every investor gets comprehensive protection and documentation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {protections.map((protection, index) => (
                  <ProtectionItem
                    key={index}
                    icon={protection.icon}
                    title={protection.title}
                    description={protection.description}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">Typical Timeline</CardTitle>
              <CardDescription>
                Our due diligence process is thorough yet efficient
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {timelineSteps.map((step, index) => (
                  <TimelineStep
                    key={index}
                    timeframe={step.timeframe}
                    title={step.title}
                    description={step.description}
                    bgColor={step.bgColor}
                    textColor={step.textColor}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Red Flags */}
          <RedFlagsSection />
        </div>
      </div>
    </div>
  );
};

export default DueDiligence;
